/**
 * One incident, during the incident.
 *
 * READING ORDER, TOP TO BOTTOM, AND WHY IT IS THIS ORDER
 *   1. What this is, and the reminder that WE DO NOT FILE IT.
 *   2. "Do this next" — one sentence and one button.
 *   3. The clocks, all of them, including the ones that have passed.
 *   4. The three (five) stage reports.
 *   5. Assessment, authority, timeline, control links.
 *
 * A stressed person reads the top of a page and stops. So the top of this page
 * is the single most urgent thing they must do, and the thing they must not
 * misunderstand. Everything that can wait is below the fold on purpose.
 *
 * DRAFTS AND LOST CONNECTIONS
 * The report editor saves on a debounce and ALSO mirrors every keystroke into
 * localStorage. If the save fails — flaky hotel wifi, a VPN dropping, a laptop
 * lid closing — the text is still in the browser and is offered back on the
 * next load. The limits are stated plainly in the interface rather than
 * implied: the browser copy is per-device and does not reach a colleague.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  INCIDENT_STATUSES, INCIDENT_SEVERITIES, INCIDENT_TRISTATE,
  INCIDENT_SUBMISSION_CHANNELS, formatDuration,
  type DeadlineView, type IncidentStage,
} from '@opencomply/shared';
import { api, ApiError, getAccessToken } from '../lib/api';
import { useAuth } from '../lib/auth';
import { downloadFile } from '../lib/download';
import {
  Card, CardHeader, Button, Pill, Modal, Field, Skeleton, ErrorNotice,
} from '../components/ui';
import { DeadlineRow, useTick, liveDeadline } from '../components/IncidentClocks';
import { useIncidentT, type IncidentKey } from '../lib/incidentStrings';
import { cn } from '../lib/cn';

// ---------------------------------------------------------------------------
//  Types mirroring the API shape
// ---------------------------------------------------------------------------

interface ReportRow {
  id: string;
  stage: IncidentStage;
  sequence: number;
  status: 'DRAFT' | 'SUBMITTED';
  submittedAt: string | null;
  submittedTo: string | null;
  submittedVia: string | null;
  submissionReference: string | null;
  latenessNote: string | null;
  content: Record<string, string | undefined>;
  warnings: string[];
}

interface TimelineRow {
  id: string;
  occurredAt: string;
  kind: string;
  summary: string;
  detail: string | null;
  actorEmail: string | null;
}

interface ControlLinkRow {
  id: string;
  note: string | null;
  organizationControl: {
    id: string;
    catalogueControl: { key: string; title: string };
  };
}

interface Incident {
  id: string;
  reference: string;
  title: string;
  description: string | null;
  severity: string;
  status: string;
  awareAt: string;
  significant: string;
  significanceNote: string | null;
  suspectedMalicious: string;
  crossBorderImpact: string;
  recipientsNotifiedAt: string | null;
  recipientsNotifiedNote: string | null;
  authorityName: string | null;
  authorityContact: string | null;
  ongoingAtFinalReport: boolean;
  reports: ReportRow[];
  events: TimelineRow[];
  controls: ControlLinkRow[];
  deadlines: DeadlineView[];
  nextAction: DeadlineView | null;
  hasMissedDeadline: boolean;
}

/** All five stages, in Article 23(4) order. */
const STAGES: IncidentStage[] = ['EARLY_WARNING', 'NOTIFICATION', 'INTERMEDIATE', 'FINAL', 'PROGRESS'];

/** Which content fields each stage leads with. Guidance, never a constraint. */
const STAGE_FIELDS: Record<IncidentStage, string[]> = {
  EARLY_WARNING: ['summary', 'crossBorderDetail', 'additionalInformation'],
  NOTIFICATION: [
    'summary', 'severityAssessment', 'impact', 'affectedServices',
    'indicatorsOfCompromise', 'crossBorderDetail', 'additionalInformation',
  ],
  INTERMEDIATE: ['ongoingStatus', 'summary', 'impact', 'mitigation', 'additionalInformation'],
  FINAL: [
    'summary', 'impact', 'rootCause', 'mitigation', 'crossBorderDetail',
    'affectedServices', 'additionalInformation',
  ],
  PROGRESS: ['ongoingStatus', 'mitigation', 'additionalInformation'],
};

const SEVERITY_TONE: Record<string, 'good' | 'warning' | 'serious' | 'critical' | 'neutral'> = {
  LOW: 'neutral', MEDIUM: 'warning', HIGH: 'serious', CRITICAL: 'critical',
};

function localValue(iso: string | Date): string {
  const date = typeof iso === 'string' ? new Date(iso) : iso;
  return new Date(date.getTime() - date.getTimezoneOffset() * 60_000).toISOString().slice(0, 16);
}


/**
 * The label on a stage's button.
 *
 * Three states, three words: never started, drafted, or already sent. A
 * submitted report is read-only, so offering to "continue the draft" would
 * promise an edit the server refuses with a 409.
 */
function stageButtonLabel(
  incident: Incident,
  stage: IncidentStage,
  tx: (key: IncidentKey) => string,
): string {
  const report = incident.reports.find((r) => r.stage === stage);
  if (report?.status === 'SUBMITTED') return tx('incident.report.view');
  if (report) return tx('incident.report.openDraft');
  return tx('incident.report.open');
}

// ---------------------------------------------------------------------------
//  Page
// ---------------------------------------------------------------------------

export function IncidentDetailPage() {
  const { id = '' } = useParams();
  const { tx, formatDateTime } = useIncidentT();
  const { t } = useTranslation();
  const { allows } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const now = useTick();

  const [openStage, setOpenStage] = useState<IncidentStage | null>(null);

  const { data: incident, isLoading, error, refetch } = useQuery({
    queryKey: ['incident', id],
    queryFn: () => api.get<Incident>(`/incidents/${id}`),
    staleTime: 10_000,
  });

  const refresh = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: ['incident', id] });
    void queryClient.invalidateQueries({ queryKey: ['incidents'] });
  }, [queryClient, id]);

  if (isLoading) {
    return (
      <div className="px-4 sm:px-6 lg:px-8 max-w-5xl mx-auto space-y-4">
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-48 w-full" />
      </div>
    );
  }

  if (error || !incident) {
    const code = error instanceof ApiError ? error.code : 'UNKNOWN_ERROR';
    return (
      <div className="px-4 sm:px-6 lg:px-8 max-w-5xl mx-auto">
        <ErrorNotice
          message={tx(`incident.error.${code}` as IncidentKey, { defaultValue: tx('incident.loadFailed') })}
          onRetry={() => void refetch()}
          retryLabel={tx('incident.retry')}
        />
      </div>
    );
  }

  const canManage = allows('incident.manage');
  const next = incident.nextAction ? liveDeadline(incident.nextAction, now) : null;

  return (
    <div className="px-4 sm:px-6 lg:px-8 max-w-5xl mx-auto space-y-5 pb-10">
      {/* ---------- Header ---------- */}
      <div>
        <Link to="/incidents" className="text-xs text-ink-muted hover:text-ink">
          ← {tx('incident.title')}
        </Link>
        <div className="flex flex-wrap items-start justify-between gap-3 mt-1">
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-mono text-xs text-ink-muted">{incident.reference}</span>
              <Pill tone={SEVERITY_TONE[incident.severity] ?? 'neutral'}>
                {tx(`incident.severity.${incident.severity}` as IncidentKey)}
              </Pill>
              <Pill tone={incident.status === 'CLOSED' ? 'good' : 'neutral'}>
                {tx(`incident.status.${incident.status}` as IncidentKey)}
              </Pill>
            </div>
            <h1 className="oc-h1 mt-1 text-balance">{incident.title}</h1>
            <p className="text-sm text-ink-muted mt-1">
              {tx('incident.clocks.awareAt', { when: formatDateTime(incident.awareAt) })}
            </p>
          </div>
        </div>
      </div>

      {/* ---------- The disclaimer. Above everything actionable. ---------- */}
      <div className="rounded-xl border border-warning/50 bg-warning/[0.08] p-3 sm:p-4">
        <p className="text-sm text-ink leading-relaxed">
          <strong>{tx('incident.notFiled.short')}</strong>
        </p>
        <p className="text-xs text-ink-secondary leading-relaxed mt-1">
          {tx('incident.notFiled.banner')}
        </p>
      </div>

      {/* ---------- One next action ---------- */}
      <Card className={cn(next?.state === 'OVERDUE' && 'border-critical/50 bg-critical/[0.05]')}>
        <CardHeader title={tx('incident.nextAction.title')} />
        {next ? (
          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className={cn('text-sm', next.state === 'OVERDUE' ? 'text-critical font-semibold' : 'text-ink')}>
              {next.state === 'OVERDUE'
                ? tx('incident.nextAction.overdue', {
                    stage: tx(`incident.stage.${next.stage}` as IncidentKey),
                  })
                : tx('incident.nextAction.open', {
                    stage: tx(`incident.stage.${next.stage}` as IncidentKey),
                    duration: formatDuration(next.msRemaining ?? 0),
                  })}
            </p>
            {canManage && (
              <Button variant="primary" onClick={() => setOpenStage(next.stage as IncidentStage)}>
                {tx('incident.report.open')}
              </Button>
            )}
          </div>
        ) : (
          <p className="text-sm text-ink-secondary">{tx('incident.nextAction.none')}</p>
        )}
      </Card>

      {/* ---------- The clocks ---------- */}
      <Card>
        <CardHeader title={tx('incident.clocks.title')} subtitle={tx('incident.clocks.basis')} />
        <div className="space-y-2">
          {incident.deadlines.map((view) => (
            <DeadlineRow
              key={view.stage}
              view={view}
              now={now}
              action={
                canManage ? (
                  <Button size="sm" onClick={() => setOpenStage(view.stage as IncidentStage)}>
                    {/* A submitted report is read-only, so offering to
                        "continue the draft" would promise an edit that the
                        server refuses. */}
                    {stageButtonLabel(incident, view.stage as IncidentStage, tx)}
                  </Button>
                ) : undefined
              }
            />
          ))}
          {/* The two stages that have no clock, stated rather than omitted. */}
          <div className="rounded-xl border border-line bg-surface p-3 sm:p-4">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-semibold text-ink">{tx('incident.stage.INTERMEDIATE')}</span>
              <span className="text-[11px] text-ink-muted font-mono">
                {tx('incident.stageCite.INTERMEDIATE')}
              </span>
              <Pill tone="neutral">{tx('incident.deadline.onRequest')}</Pill>
            </div>
            <p className="text-xs text-ink-secondary mt-1">{tx('incident.deadline.onRequestDetail')}</p>
            {canManage && (
              <div className="mt-2 flex gap-2">
                <Button size="sm" onClick={() => setOpenStage('INTERMEDIATE')}>
                  {tx('incident.report.open')}
                </Button>
                <Button size="sm" onClick={() => setOpenStage('PROGRESS')}>
                  {tx('incident.stage.PROGRESS')}
                </Button>
              </div>
            )}
          </div>
        </div>
      </Card>

      {/* ---------- Stage reports ---------- */}
      <Card>
        <CardHeader title={tx('incident.export.title')} subtitle={tx('incident.export.body')} />
        <div className="space-y-2">
          {STAGES.map((stage) => {
            const report = incident.reports.find((r) => r.stage === stage);
            return (
              <div
                key={stage}
                className="rounded-xl border border-line p-3 flex flex-wrap items-center justify-between gap-2"
              >
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium text-ink">
                      {tx(`incident.stage.${stage}` as IncidentKey)}
                    </span>
                    <Pill tone={report?.status === 'SUBMITTED' ? 'good' : report ? 'warning' : 'neutral'}>
                      {report?.status === 'SUBMITTED'
                        ? tx('incident.report.submitted')
                        : report
                          ? tx('incident.report.draft')
                          : tx('incident.report.notStarted')}
                    </Pill>
                  </div>
                  {report?.status === 'SUBMITTED' && (
                    <p className="text-xs text-ink-muted mt-0.5">
                      {formatDateTime(report.submittedAt)} → {report.submittedTo}
                      {report.submissionReference ? ` · ${report.submissionReference}` : ''}
                    </p>
                  )}
                </div>
                <div className="flex flex-wrap gap-2">
                  <ExportButtons incidentId={incident.id} stage={stage} />
                  {canManage && (
                    <Button size="sm" variant="secondary" onClick={() => setOpenStage(stage)}>
                      {stageButtonLabel(incident, stage, tx)}
                    </Button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </Card>

      <AssessmentCard incident={incident} canManage={canManage} onSaved={refresh} />
      <TimelineCard incident={incident} canManage={canManage} onSaved={refresh} />
      <ControlsCard incident={incident} canManage={canManage} onSaved={refresh} />

      {openStage && (
        <ReportEditor
          incident={incident}
          stage={openStage}
          onClose={() => setOpenStage(null)}
          onChanged={refresh}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
//  Exports
// ---------------------------------------------------------------------------

/**
 * The three download buttons.
 *
 * Uses the shared `downloadFile`, which fetches with the bearer token (a plain
 * <a href> cannot carry one) and revokes the object URL afterwards.
 */
function ExportButtons({ incidentId, stage }: { incidentId: string; stage: IncidentStage }) {
  const { tx } = useIncidentT();
  const [busy, setBusy] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);

  const download = async (format: 'pdf' | 'txt' | 'csv') => {
    setBusy(format);
    setFailed(false);
    const controller = new AbortController();
    try {
      await downloadFile(
        `/incidents/${incidentId}/reports/${stage}/export?format=${format}`,
        `${stage.toLowerCase()}.${format}`,
        '/notifications/unread-count',
        controller.signal,
      );
    } catch {
      setFailed(true);
    } finally {
      setBusy(null);
    }
  };

  return (
    <>
      <Button size="sm" disabled={busy !== null} onClick={() => void download('pdf')}>
        {busy === 'pdf' ? '…' : 'PDF'}
      </Button>
      <Button size="sm" disabled={busy !== null} onClick={() => void download('txt')}>
        {busy === 'txt' ? '…' : tx('incident.export.txt')}
      </Button>
      {failed && <span role="alert" className="text-xs text-critical">{tx('incident.export.failed')}</span>}
    </>
  );
}

// ---------------------------------------------------------------------------
//  Report editor
// ---------------------------------------------------------------------------

/**
 * The thing someone types into at 02:00.
 *
 * AUTOSAVE, TWO LAYERS
 *   1. A debounced PATCH to the server, which merges rather than replaces.
 *   2. A synchronous mirror into localStorage on every change.
 *
 * Layer 2 exists because layer 1 can fail, and the failure mode it guards
 * against is the realistic one: the connection drops mid-incident and the
 * laptop is closed. On the next open, the browser copy is offered back with the
 * time it was kept, rather than silently overwriting what the server has — the
 * person decides which is right, because only they know.
 */
function ReportEditor({
  incident,
  stage,
  onClose,
  onChanged,
}: {
  incident: Incident;
  stage: IncidentStage;
  onClose: () => void;
  onChanged: () => void;
}) {
  const { tx, formatDateTime } = useIncidentT();
  const { t } = useTranslation();
  const { allows } = useAuth();
  const now = useTick();

  const storageKey = `oc-incident-draft:${incident.id}:${stage}`;

  const [content, setContent] = useState<Record<string, string>>({});
  const [loaded, setLoaded] = useState(false);
  const [restoredFrom, setRestoredFrom] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<Date | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveFailed, setSaveFailed] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);
  const [warnings, setWarnings] = useState<string[]>([]);
  const [submitOpen, setSubmitOpen] = useState(false);

  const report = incident.reports.find((r) => r.stage === stage);
  const locked = report?.status === 'SUBMITTED';
  const deadline = incident.deadlines.find((d) => d.stage === stage);
  const live = deadline ? liveDeadline(deadline, now) : null;

  // --- Open (and create) the draft on mount --------------------------------
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const opened = await api.post<ReportRow>(`/incidents/${incident.id}/reports/${stage}`);
        if (cancelled) return;
        const serverContent = (opened.content ?? {}) as Record<string, string>;

        // A browser copy only wins if it actually differs, so a clean reload
        // never shows a spurious "restored" banner.
        let next = serverContent;
        try {
          const raw = localStorage.getItem(storageKey);
          if (raw) {
            const cached = JSON.parse(raw) as { at: string; content: Record<string, string> };
            if (JSON.stringify(cached.content) !== JSON.stringify(serverContent)) {
              next = { ...serverContent, ...cached.content };
              setRestoredFrom(cached.at);
            }
          }
        } catch {
          // Private browsing, cleared storage, a quota error — all fine. The
          // server copy is authoritative and the feature degrades to that.
        }

        setContent(next);
        setWarnings(opened.warnings ?? []);
        setLoaded(true);
      } catch {
        // An empty form that LOOKS like a clean draft is the dangerous failure:
        // somebody types into it, believing they are editing the real one. Say
        // the load failed instead.
        if (!cancelled) {
          setLoadFailed(true);
          setLoaded(true);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
    // Intentionally keyed on the identity of the draft only.
  }, [incident.id, stage, storageKey]);

  // --- Debounced save ------------------------------------------------------
  const timer = useRef<number | null>(null);
  const pending = useRef<Record<string, string> | null>(null);

  const flush = useCallback(async () => {
    const payload = pending.current;
    if (!payload || locked) return;
    pending.current = null;
    setSaving(true);
    try {
      const saved = await api.patch<ReportRow>(
        `/incidents/${incident.id}/reports/${stage}`,
        { content: payload },
      );
      setWarnings(saved.warnings ?? []);
      setSavedAt(new Date());
      setSaveFailed(false);
      // The server now has it, so the browser copy has done its job.
      try {
        localStorage.removeItem(storageKey);
      } catch { /* storage may be unavailable; nothing depends on this */ }
    } catch {
      // Deliberately NOT cleared: the localStorage copy is the recovery path.
      setSaveFailed(true);
    } finally {
      setSaving(false);
    }
  }, [incident.id, stage, locked, storageKey]);

  const change = (field: string, value: string) => {
    const next = { ...content, [field]: value };
    setContent(next);

    // Layer 2, synchronous and immediate. This is the copy that survives a
    // closed lid, so it must not wait for the debounce.
    try {
      localStorage.setItem(storageKey, JSON.stringify({ at: new Date().toISOString(), content: next }));
    } catch { /* see above */ }

    pending.current = next;
    if (timer.current) window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => void flush(), 800);
  };

  // Flush on unmount so closing the dialog never loses the last 800ms.
  useEffect(() => () => {
    if (timer.current) window.clearTimeout(timer.current);
    void flush();
  }, [flush]);

  const fields = STAGE_FIELDS[stage];

  return (
    <Modal
      open
      onClose={onClose}
      title={tx('incident.report.editorTitle', {
        stage: tx(`incident.stage.${stage}` as IncidentKey),
        reference: incident.reference,
      })}
      footer={
        <>
          <Button onClick={onClose}>{t('common.close')}</Button>
          {!locked && allows('incident.submit') && (
            <Button variant="primary" onClick={() => setSubmitOpen(true)}>
              {tx('incident.submit.action')}
            </Button>
          )}
        </>
      }
    >
      {/* The disclaimer, again, directly above the fields. */}
      <div className="rounded-lg border border-warning/40 bg-warning/[0.07] p-3 mb-4">
        <p className="text-xs text-ink-secondary leading-relaxed">
          <strong className="text-ink">{tx('incident.notFiled.short')}</strong>{' '}
          {tx('incident.notFiled.banner')}
        </p>
      </div>

      {live && (
        <div className="mb-4">
          <DeadlineRow view={live} now={now} />
        </div>
      )}

      {locked && (
        <div className="rounded-lg border border-line bg-surface-sunken p-3 mb-4">
          <p className="text-sm font-medium text-ink">{tx('incident.report.lockedTitle')}</p>
          <p className="text-xs text-ink-secondary mt-1">{tx('incident.report.lockedBody')}</p>
          <dl className="mt-2 text-xs text-ink-secondary space-y-0.5">
            <div>{tx('incident.submit.when')} {formatDateTime(report?.submittedAt)}</div>
            <div>{tx('incident.submit.to')} {report?.submittedTo}</div>
            {report?.submissionReference && (
              <div>{tx('incident.submit.reference')} {report.submissionReference}</div>
            )}
            {report?.latenessNote && (
              <div>{tx('incident.submit.lateness')} {report.latenessNote}</div>
            )}
          </dl>
        </div>
      )}

      {restoredFrom && (
        <p className="text-xs text-brand mb-3" role="status">
          {tx('incident.report.restored', { when: formatDateTime(restoredFrom) })}
        </p>
      )}

      {!loaded && <Skeleton className="h-40 w-full" />}

      {loadFailed && (
        <ErrorNotice
          message={tx('incident.loadFailed')}
          onRetry={() => window.location.reload()}
          retryLabel={tx('incident.retry')}
        />
      )}

      {loaded && !loadFailed && (
        <>
          {fields.map((field) => (
            <Field
              key={field}
              label={tx(`incident.field.${field}` as IncidentKey)}
              hint={tx(`incident.field.${field}Hint` as IncidentKey, { defaultValue: '' }) || undefined}
              htmlFor={`incident-field-${field}`}
            >
              <textarea
                id={`incident-field-${field}`}
                className="oc-input min-h-24"
                value={content[field] ?? ''}
                maxLength={20_000}
                disabled={locked}
                onChange={(e) => change(field, e.target.value)}
              />
            </Field>
          ))}

          <div className="border-t border-line pt-3 mt-1">
            <p className="text-xs text-ink-muted mb-2">{tx('incident.field.contactPrefilled')}</p>
            <div className="grid sm:grid-cols-3 gap-x-3">
              {(['contactName', 'contactEmail', 'contactPhone'] as const).map((field) => (
                <Field
                  key={field}
                  label={tx(`incident.field.${field}` as IncidentKey)}
                  htmlFor={`incident-field-${field}`}
                >
                  <input
                    id={`incident-field-${field}`}
                    className="oc-input"
                    value={content[field] ?? ''}
                    disabled={locked}
                    onChange={(e) => change(field, e.target.value)}
                  />
                </Field>
              ))}
            </div>
          </div>

          {/* Save state, stated rather than implied by a spinner alone. */}
          <p className={cn('text-xs', saveFailed ? 'text-critical' : 'text-ink-muted')} role="status">
            {saveFailed
              ? tx('incident.report.saveFailed')
              : saving
                ? tx('incident.report.saving')
                : savedAt
                  ? tx('incident.report.savedAt', { when: formatDateTime(savedAt) })
                  : ''}
          </p>

          {/* WARNINGS. Never a block. */}
          {!locked && (
            <div className="rounded-lg border border-line bg-surface-sunken p-3 mt-3">
              <p className="text-xs font-medium text-ink">
                {warnings.length > 0 ? tx('incident.warnings.title') : tx('incident.warnings.none')}
              </p>
              {warnings.length > 0 && (
                <>
                  <ul className="mt-1 text-xs text-ink-secondary list-disc pl-4">
                    {warnings.map((w) => (
                      <li key={w}>{tx(`incident.field.${w}` as IncidentKey, { defaultValue: w })}</li>
                    ))}
                  </ul>
                  <p className="text-xs text-ink-muted mt-1.5">{tx('incident.warnings.body')}</p>
                </>
              )}
            </div>
          )}

          <div className="flex flex-wrap gap-2 mt-3">
            <ExportButtons incidentId={incident.id} stage={stage} />
          </div>
        </>
      )}

      {submitOpen && (
        <RecordSubmissionModal
          incident={incident}
          stage={stage}
          overdue={live?.state === 'OVERDUE'}
          onClose={() => setSubmitOpen(false)}
          onRecorded={() => {
            setSubmitOpen(false);
            onChanged();
            onClose();
          }}
        />
      )}
    </Modal>
  );
}

// ---------------------------------------------------------------------------
//  Recording a submission
// ---------------------------------------------------------------------------

/**
 * "I sent this."
 *
 * The acknowledgement checkbox is required by the server too, mirroring
 * `submission.finalize`. What it acknowledges is unusual and deliberate: not
 * that the customer is sure the report is complete, but that THEY are the one
 * filing it and we are not.
 *
 * Nothing here checks completeness. A customer can record a submission of an
 * entirely empty draft, because at hour 23 that is their call.
 */
function RecordSubmissionModal({
  incident,
  stage,
  overdue,
  onClose,
  onRecorded,
}: {
  incident: Incident;
  stage: IncidentStage;
  overdue?: boolean;
  onClose: () => void;
  onRecorded: () => void;
}) {
  const { tx } = useIncidentT();
  const { t } = useTranslation();

  const [submittedAt, setSubmittedAt] = useState(() => localValue(new Date()));
  const [submittedTo, setSubmittedTo] = useState(incident.authorityName ?? '');
  const [via, setVia] = useState<(typeof INCIDENT_SUBMISSION_CHANNELS)[number]>('PORTAL');
  const [reference, setReference] = useState('');
  const [latenessNote, setLatenessNote] = useState('');
  const [acknowledged, setAcknowledged] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: () =>
      api.post(`/incidents/${incident.id}/reports/${stage}/record-submission`, {
        submittedAt: new Date(submittedAt).toISOString(),
        submittedTo: submittedTo.trim(),
        submittedVia: via,
        ...(reference.trim() ? { submissionReference: reference.trim() } : {}),
        ...(latenessNote.trim() ? { latenessNote: latenessNote.trim() } : {}),
        acknowledged: true,
      }),
    onSuccess: onRecorded,
    onError: (err) => {
      const code = err instanceof ApiError ? err.code : 'UNKNOWN_ERROR';
      setError(tx(`incident.error.${code}` as IncidentKey, { defaultValue: tx('incident.submit.failed') }));
    },
  });

  return (
    <Modal
      open
      onClose={onClose}
      title={tx('incident.notFiled.confirmTitle')}
      footer={
        <>
          <Button onClick={onClose}>{t('common.cancel')}</Button>
          <Button
            variant="primary"
            disabled={!acknowledged || !submittedTo.trim() || mutation.isPending}
            onClick={() => mutation.mutate()}
          >
            {tx('incident.submit.confirm')}
          </Button>
        </>
      }
    >
      <div className="rounded-lg border border-warning/50 bg-warning/[0.08] p-3 mb-4">
        <p className="text-sm text-ink leading-relaxed">{tx('incident.notFiled.confirmBody')}</p>
      </div>

      <Field label={tx('incident.submit.when')} hint={tx('incident.submit.whenHint')} required htmlFor="submitted-at">
        <input
          id="submitted-at"
          type="datetime-local"
          className="oc-input"
          value={submittedAt}
          onChange={(e) => setSubmittedAt(e.target.value)}
        />
      </Field>

      <Field label={tx('incident.submit.to')} hint={tx('incident.submit.toHint')} required htmlFor="submitted-to">
        <input
          id="submitted-to"
          className="oc-input"
          value={submittedTo}
          maxLength={500}
          onChange={(e) => setSubmittedTo(e.target.value)}
        />
      </Field>

      <Field label={tx('incident.submit.via')} required htmlFor="submitted-via">
        <select
          id="submitted-via"
          className="oc-input"
          value={via}
          onChange={(e) => setVia(e.target.value as typeof via)}
        >
          {INCIDENT_SUBMISSION_CHANNELS.map((c) => (
            <option key={c} value={c}>{tx(`incident.submit.via.${c}` as IncidentKey)}</option>
          ))}
        </select>
      </Field>

      <Field label={tx('incident.submit.reference')} htmlFor="submitted-ref">
        <input
          id="submitted-ref"
          className="oc-input"
          value={reference}
          maxLength={255}
          onChange={(e) => setReference(e.target.value)}
        />
      </Field>

      {/* Only prompted when it is actually late — otherwise it is noise. */}
      {overdue && (
        <Field
          label={tx('incident.submit.lateness')}
          hint={tx('incident.submit.latenessHint')}
          htmlFor="lateness-note"
        >
          <textarea
            id="lateness-note"
            className="oc-input min-h-20"
            value={latenessNote}
            maxLength={5_000}
            onChange={(e) => setLatenessNote(e.target.value)}
          />
        </Field>
      )}

      <label className="flex items-start gap-2 text-sm text-ink cursor-pointer mt-2">
        <input
          type="checkbox"
          className="mt-0.5"
          checked={acknowledged}
          onChange={(e) => setAcknowledged(e.target.checked)}
        />
        <span>{tx('incident.notFiled.acknowledge')}</span>
      </label>

      {error && <p role="alert" className="text-sm text-critical mt-2">{error}</p>}
    </Modal>
  );
}

// ---------------------------------------------------------------------------
//  Assessment
// ---------------------------------------------------------------------------

/** Significance, maliciousness, cross-border reach, recipients, authority. */
function AssessmentCard({
  incident,
  canManage,
  onSaved,
}: {
  incident: Incident;
  canManage: boolean;
  onSaved: () => void;
}) {
  const { tx } = useIncidentT();
  const { t } = useTranslation();
  const [draft, setDraft] = useState({
    significant: incident.significant,
    significanceNote: incident.significanceNote ?? '',
    suspectedMalicious: incident.suspectedMalicious,
    crossBorderImpact: incident.crossBorderImpact,
    authorityName: incident.authorityName ?? '',
    authorityContact: incident.authorityContact ?? '',
    status: incident.status,
    severity: incident.severity,
  });
  const [saved, setSaved] = useState(false);

  const mutation = useMutation({
    mutationFn: () =>
      api.patch(`/incidents/${incident.id}`, {
        significant: draft.significant,
        significanceNote: draft.significanceNote.trim() || null,
        suspectedMalicious: draft.suspectedMalicious,
        crossBorderImpact: draft.crossBorderImpact,
        authorityName: draft.authorityName.trim() || null,
        authorityContact: draft.authorityContact.trim() || null,
        status: draft.status,
        severity: draft.severity,
      }),
    onSuccess: () => {
      setSaved(true);
      onSaved();
      window.setTimeout(() => setSaved(false), 2500);
    },
  });

  const tri = (key: 'significant' | 'suspectedMalicious' | 'crossBorderImpact', label: string, hint?: string) => (
    <Field label={label} hint={hint} htmlFor={`assess-${key}`}>
      <select
        id={`assess-${key}`}
        className="oc-input"
        value={draft[key]}
        disabled={!canManage}
        onChange={(e) => setDraft({ ...draft, [key]: e.target.value })}
      >
        {INCIDENT_TRISTATE.map((v) => (
          <option key={v} value={v}>{tx(`incident.tristate.${v}` as IncidentKey)}</option>
        ))}
      </select>
    </Field>
  );

  return (
    <Card>
      <CardHeader title={tx('incident.assess.title')} />

      <div className="rounded-lg border border-line bg-surface-sunken p-3 mb-4">
        <p className="text-xs font-medium text-ink">{tx('incident.national.title')}</p>
        <p className="text-xs text-ink-secondary mt-1 leading-relaxed">{tx('incident.national.body')}</p>
      </div>

      <div className="grid sm:grid-cols-2 gap-x-4">
        <Field label={tx('incident.declare.severity')} htmlFor="assess-severity">
          <select
            id="assess-severity"
            className="oc-input"
            value={draft.severity}
            disabled={!canManage}
            onChange={(e) => setDraft({ ...draft, severity: e.target.value })}
          >
            {INCIDENT_SEVERITIES.map((s) => (
              <option key={s} value={s}>{tx(`incident.severity.${s}` as IncidentKey)}</option>
            ))}
          </select>
        </Field>
        <Field
          label={tx('incident.assess.statusLabel')}
          hint={tx('incident.assess.statusHint')}
          htmlFor="assess-status"
        >
          <select
            id="assess-status"
            className="oc-input"
            value={draft.status}
            disabled={!canManage}
            onChange={(e) => setDraft({ ...draft, status: e.target.value })}
          >
            {INCIDENT_STATUSES.map((s) => (
              <option key={s} value={s}>{tx(`incident.status.${s}` as IncidentKey)}</option>
            ))}
          </select>
        </Field>
      </div>

      {tri('significant', tx('incident.assess.significant'), tx('incident.assess.significantHint'))}

      <Field label={tx('incident.assess.significanceNote')} htmlFor="assess-note">
        <textarea
          id="assess-note"
          className="oc-input min-h-20"
          value={draft.significanceNote}
          disabled={!canManage}
          maxLength={5_000}
          onChange={(e) => setDraft({ ...draft, significanceNote: e.target.value })}
        />
      </Field>

      <div className="grid sm:grid-cols-2 gap-x-4">
        {tri('suspectedMalicious', tx('incident.assess.malicious'), tx('incident.assess.maliciousHint'))}
        {tri('crossBorderImpact', tx('incident.assess.crossBorder'))}
      </div>

      <Field
        label={tx('incident.national.authority')}
        hint={tx('incident.national.authorityHint')}
        htmlFor="assess-authority"
      >
        <input
          id="assess-authority"
          className="oc-input"
          value={draft.authorityName}
          disabled={!canManage}
          maxLength={255}
          onChange={(e) => setDraft({ ...draft, authorityName: e.target.value })}
        />
      </Field>

      <Field label={tx('incident.national.contact')} htmlFor="assess-contact">
        <input
          id="assess-contact"
          className="oc-input"
          value={draft.authorityContact}
          disabled={!canManage}
          maxLength={2_000}
          onChange={(e) => setDraft({ ...draft, authorityContact: e.target.value })}
        />
      </Field>

      {canManage && (
        <div className="flex items-center gap-3">
          <Button variant="primary" disabled={mutation.isPending} onClick={() => mutation.mutate()}>
            {mutation.isPending ? t('common.saving') : t('common.save')}
          </Button>
          {saved && <span className="text-xs text-good" role="status">{t('common.save')} ✓</span>}
        </div>
      )}
    </Card>
  );
}

// ---------------------------------------------------------------------------
//  Timeline
// ---------------------------------------------------------------------------

function TimelineCard({
  incident,
  canManage,
  onSaved,
}: {
  incident: Incident;
  canManage: boolean;
  onSaved: () => void;
}) {
  const { tx, formatDateTime } = useIncidentT();
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [occurredAt, setOccurredAt] = useState(() => localValue(new Date()));
  const [summary, setSummary] = useState('');
  const [detail, setDetail] = useState('');

  const mutation = useMutation({
    mutationFn: () =>
      api.post(`/incidents/${incident.id}/timeline`, {
        occurredAt: new Date(occurredAt).toISOString(),
        summary: summary.trim(),
        ...(detail.trim() ? { detail: detail.trim() } : {}),
      }),
    onSuccess: () => {
      setSummary('');
      setDetail('');
      setOpen(false);
      onSaved();
    },
  });

  // Newest first on screen; the EXPORT prints them oldest first, because a
  // report reads as a narrative and a screen reads as a feed.
  const events = useMemo(
    () => [...incident.events].sort(
      (a, b) => new Date(b.occurredAt).getTime() - new Date(a.occurredAt).getTime(),
    ),
    [incident.events],
  );

  return (
    <Card>
      <CardHeader
        title={tx('incident.timeline.title')}
        subtitle={tx('incident.timeline.body')}
        action={
          canManage ? (
            <Button size="sm" onClick={() => setOpen(true)}>{tx('incident.timeline.add')}</Button>
          ) : undefined
        }
      />

      {events.length === 0 && <p className="oc-muted">{tx('incident.timeline.empty')}</p>}

      <ol className="space-y-3">
        {events.map((event) => (
          <li key={event.id} className="flex gap-3">
            <div className="shrink-0 w-1 rounded-full bg-line" aria-hidden />
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-xs font-mono text-ink-muted">
                  {formatDateTime(event.occurredAt)}
                </span>
                <Pill tone="neutral">
                  {tx(`incident.timeline.kind.${event.kind}` as IncidentKey, { defaultValue: event.kind })}
                </Pill>
              </div>
              <p className="text-sm text-ink mt-0.5">{event.summary}</p>
              {event.detail && (
                <p className="text-xs text-ink-secondary mt-0.5 whitespace-pre-line">{event.detail}</p>
              )}
              <p className="text-[11px] text-ink-muted mt-0.5">
                {event.actorEmail
                  ? tx('incident.timeline.by', { who: event.actorEmail })
                  : tx('incident.timeline.system')}
              </p>
            </div>
          </li>
        ))}
      </ol>

      <Modal
        open={open}
        onClose={() => setOpen(false)}
        title={tx('incident.timeline.add')}
        footer={
          <>
            <Button onClick={() => setOpen(false)}>{t('common.cancel')}</Button>
            <Button
              variant="primary"
              disabled={!summary.trim() || mutation.isPending}
              onClick={() => mutation.mutate()}
            >
              {tx('incident.timeline.save')}
            </Button>
          </>
        }
      >
        <Field label={tx('incident.timeline.when')} hint={tx('incident.timeline.whenHint')} required htmlFor="tl-when">
          <input
            id="tl-when"
            type="datetime-local"
            className="oc-input"
            value={occurredAt}
            onChange={(e) => setOccurredAt(e.target.value)}
          />
        </Field>
        <Field label={tx('incident.timeline.summary')} required htmlFor="tl-summary">
          <input
            id="tl-summary"
            className="oc-input"
            value={summary}
            maxLength={1_000}
            onChange={(e) => setSummary(e.target.value)}
          />
        </Field>
        <Field label={tx('incident.timeline.detail')} htmlFor="tl-detail">
          <textarea
            id="tl-detail"
            className="oc-input min-h-20"
            value={detail}
            maxLength={10_000}
            onChange={(e) => setDetail(e.target.value)}
          />
        </Field>
      </Modal>
    </Card>
  );
}

// ---------------------------------------------------------------------------
//  Control links — the incident as evidence
// ---------------------------------------------------------------------------

interface AvailableControl {
  key: string;
  title: string;
  domain: string;
  status: string | null;
  suggested: boolean;
}

function ControlsCard({
  incident,
  canManage,
  onSaved,
}: {
  incident: Incident;
  canManage: boolean;
  onSaved: () => void;
}) {
  const { tx } = useIncidentT();
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [chosen, setChosen] = useState('');

  const { data } = useQuery({
    queryKey: ['incident', incident.id, 'controls-available'],
    queryFn: () => api.get<{ items: AvailableControl[] }>(`/incidents/${incident.id}/controls/available`),
    enabled: open,
  });

  const link = useMutation({
    mutationFn: () => api.post(`/incidents/${incident.id}/controls`, { catalogueControlKey: chosen }),
    onSuccess: () => {
      setOpen(false);
      setChosen('');
      onSaved();
    },
  });

  const unlink = useMutation({
    mutationFn: (linkId: string) => api.delete(`/incidents/${incident.id}/controls/${linkId}`),
    onSuccess: onSaved,
  });

  const available = data?.items ?? [];

  return (
    <Card>
      <CardHeader
        title={tx('incident.controls.title')}
        subtitle={tx('incident.controls.body')}
        action={
          canManage ? (
            <Button size="sm" onClick={() => setOpen(true)}>{tx('incident.controls.add')}</Button>
          ) : undefined
        }
      />

      {incident.controls.length === 0 && <p className="oc-muted">{tx('incident.controls.none')}</p>}

      <ul className="space-y-2">
        {incident.controls.map((entry) => (
          <li
            key={entry.id}
            className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-line p-2.5"
          >
            <div className="min-w-0">
              <span className="font-mono text-xs text-ink-muted">
                {entry.organizationControl.catalogueControl.key}
              </span>
              <p className="text-sm text-ink">{entry.organizationControl.catalogueControl.title}</p>
            </div>
            {canManage && (
              <Button size="sm" variant="ghost" onClick={() => unlink.mutate(entry.id)}>
                {tx('incident.controls.remove')}
              </Button>
            )}
          </li>
        ))}
      </ul>

      <Modal
        open={open}
        onClose={() => setOpen(false)}
        title={tx('incident.controls.add')}
        footer={
          <>
            <Button onClick={() => setOpen(false)}>{t('common.cancel')}</Button>
            <Button variant="primary" disabled={!chosen || link.isPending} onClick={() => link.mutate()}>
              {tx('incident.controls.add')}
            </Button>
          </>
        }
      >
        <Field label={tx('incident.controls.chooseLabel')} htmlFor="control-choice">
          <select
            id="control-choice"
            className="oc-input"
            value={chosen}
            onChange={(e) => setChosen(e.target.value)}
          >
            <option value="">—</option>
            {available.map((c) => (
              <option key={c.key} value={c.key}>
                {c.suggested ? `★ ${c.key}` : c.key} — {c.title}
              </option>
            ))}
          </select>
        </Field>
        <p className="text-xs text-ink-muted">★ {tx('incident.controls.suggested')}</p>
      </Modal>
    </Card>
  );
}
